/**
 * Locale-aware relative-time formatting via Intl.RelativeTimeFormat.
 *
 * WS6 invariant: the backend sends raw ISO timestamps only ("no baked
 * strings") — humanizing them ("5 minutes ago") is display-time,
 * locale-dependent work, so it lives here rather than on the API response.
 * Deliberately independent of react-i18next `t()`: Intl already knows each
 * locale's plural rules (1 minuto / 5 minutos) and its "now"/"yesterday"
 * special cases, so there is no per-language key to maintain.
 */
import { resolveLocale } from './utils';

const UNITS: ReadonlyArray<{ unit: Intl.RelativeTimeFormatUnit; ms: number }> = [
  { unit: 'year', ms: 365 * 24 * 60 * 60 * 1000 },
  { unit: 'month', ms: 30 * 24 * 60 * 60 * 1000 },
  { unit: 'week', ms: 7 * 24 * 60 * 60 * 1000 },
  { unit: 'day', ms: 24 * 60 * 60 * 1000 },
  { unit: 'hour', ms: 60 * 60 * 1000 },
  { unit: 'minute', ms: 60 * 1000 },
];

/**
 * Formats an ISO timestamp as a locale-aware relative string
 * ("5 minutes ago" / "in 3 hours" / "yesterday").
 *
 * Returns "—" for a missing or unparseable timestamp — the absence-of-data
 * invariant: never fabricate a time from `Date.now()`.
 */
export function formatRelativeTime(
  isoTimestamp: string | null | undefined,
  nowMs: number = Date.now()
): string {
  if (!isoTimestamp) return '—';

  const then = new Date(isoTimestamp).getTime();
  if (Number.isNaN(then)) return '—';

  const diffMs = then - nowMs;
  const absMs = Math.abs(diffMs);
  const rtf = new Intl.RelativeTimeFormat(resolveLocale(), { numeric: 'auto' });

  // Sub-minute deltas collapse to "now" (Intl's value-0 special case) rather
  // than a jittery "23 seconds ago" that changes every render.
  if (absMs < 60_000) return rtf.format(0, 'second');

  const bucket = UNITS.find(({ ms }) => absMs >= ms) ?? UNITS[UNITS.length - 1]!;
  const value = Math.round(diffMs / bucket.ms);
  return rtf.format(value, bucket.unit);
}
